import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
    Card, 
    Typography, 
    Button, 
    message, 
    Result,
    Spin,
    Tooltip
} from 'antd';
import { 
    CommentOutlined,
    DownloadOutlined, 
    EyeOutlined,
    FolderOpenOutlined,
    MailOutlined,
    RollbackOutlined,
    CloseOutlined
} from '@ant-design/icons';
import axios from '../../utility/axios';
import dayjs from 'dayjs';
import { isConvertible } from '../../utility/fileTypes';
import AttachmentPreview from '../../contexts/AttachmentPreview';
import AccessCodeModal from '../common/AccessCodeModal';
import '../../styles/messages/MessageView.css';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useBecomeGuest } from '../../contexts/BecomeGuestContext';

const { Title, Text } = Typography;

const MessageView = ({ mailboxHref }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { setCanBecomeGuest } = useBecomeGuest();
    const { unique_identifier } = useParams();
    const [messageData, setMessageData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewerEmail, setViewerEmail] = useState(null);
    const [requiresAccessCode, setRequiresAccessCode] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Resizable columns state (body + attachments)
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('mailboxview_column_widths');
        return saved ? JSON.parse(saved) : { body: 60, attachments: 40 };
    });
    const [attachmentPreviewDoc, setAttachmentPreviewDoc] = useState(null);
    const [isResizing, setIsResizing] = useState(false);
    const contentRowRef = useRef(null);

    // Helper function to check if there are multiple recipients
    const hasMultipleRecipients = (recipients) => {
        if (!recipients) return false;
        // Check if there's a comma, semicolon or other separator in the recipients string
        if (typeof recipients === 'string') {
            // Split on common email separators (comma, semicolon, space)
            const emailList = recipients.split(/[,;\s]+/).map(email => email.trim()).filter(email => email !== '');
            return emailList.length > 1;
        }
        // If recipients is an array, check its length
        if (Array.isArray(recipients)) {
            return recipients.length > 1;
        }
        return false;
    };
    
    const fetchMessage = async () => {
        try {
            setLoading(true);
            const headers = {};
            
            // Check if we should preserve the view count
            let shouldPreserveViews = false;
            
            // First check if preserveViews is explicitly set in location state
            if (location.state?.preserveViews === true) {
                shouldPreserveViews = true;
                // Clear the state after using it to prevent preserving views on future navigations
                window.history.replaceState({}, document.title);
            } else if (location.state?.preserveViews === false) {
                // If preserveViews is explicitly set to false, we should NOT preserve views
                // This takes precedence over the session storage check
                shouldPreserveViews = false;
                // Clear the state after using it
                window.history.replaceState({}, document.title);
            } else {
                // Case 2: No explicit state - check if we've already viewed this message in this session
                const viewedMessagesStr = sessionStorage.getItem('viewedMessages') || '[]';
                const viewedMessages = JSON.parse(viewedMessagesStr);
                if (viewedMessages.includes(unique_identifier)) {
                    shouldPreserveViews = true;
                } else {
                    // Add this message to the list of viewed messages in this session
                    viewedMessages.push(unique_identifier);
                    sessionStorage.setItem('viewedMessages', JSON.stringify(viewedMessages));
                }
            }
            
            // Set header if we should preserve views
            if (shouldPreserveViews) {
                headers['X-Preserve-Views'] = 'true';
            }
            
            let endpoint = `/api/link/${unique_identifier}/`;
            const path = window.location.pathname;
            
            // Check if this is a reply view
            if (path.includes('/reply/')) {
                endpoint = `/api/link/reply/${unique_identifier}/`;
                
                // Check if this is a reply-all request
                if (window.location.search.includes('reply_all=true')) {
                    endpoint += '?reply_all=true';
                }
            }
            
            const response = await axios.get(endpoint, {
                headers
            });
            
            setMessageData(response.data.message);
            setViewerEmail(response.data.viewer_email);
            setError(null);
            setRequiresAccessCode(false);
        } catch (err) {
            if (err.response?.status === 401 && err.response?.data?.requires_verification) {
                setRequiresAccessCode(true);
                setError(null);
            } else if (err.response?.status === 410) {
                setError(t('messages.view.errorExpired'));
            } else if (err.response?.status === 404) {
                setError(t('messages.view.errorNotFound'));
            } else {
                setError(t('messages.view.errorFailedToLoad'));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMessage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unique_identifier]);

    const handleAccessCodeSuccess = () => {
        setRequiresAccessCode(false);
        setCanBecomeGuest?.(true);
        fetchMessage();
    };

    // Handle column resize
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing || !contentRowRef.current) return;
            const containerRect = contentRowRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            let bodyWidth = Math.max(30, Math.min(80, mousePercent));
            const attachmentsWidth = 100 - bodyWidth;
            const newWidths = { body: bodyWidth, attachments: attachmentsWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('mailboxview_column_widths', JSON.stringify(newWidths));
        };
        const handleMouseUp = () => {
            if (isResizing) {
                setIsResizing(false);
                document.body.classList.remove('mailbox-view-resizing');
            }
        };
        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('mailbox-view-resizing');
        }
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('mailbox-view-resizing');
        };
    }, [isResizing]);

    const handleDownload = async (attachmentId, fileName) => {
        try {
            const response = await axios.get(`/api/messages/download/${attachmentId}/`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handleDownloadAll = async () => {
        try {
            // Use messageData.unique_identifier to ensure we're using the correct message ID
            const response = await axios.get(`/api/messages/${messageData.unique_identifier}/download-all/`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'attachments.zip');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            message.error(t('messages.view.errorFailedDownload'));
        }
    };

    const handleReply = () => {
        // Navigate to the reply URL with state to preserve views
        const replyUrl = `/link/reply/${unique_identifier}?action=reply`;
        navigate(replyUrl, { state: { preserveViews: true } });
    };

    const handleReplyAll = () => {
        // Navigate to the reply URL with reply_all flag and state to preserve views
        const replyAllUrl = `/link/reply/${unique_identifier}?reply_all=true&action=reply_all`;
        navigate(replyAllUrl, { state: { preserveViews: true } });
    };

    // Check if current viewer is only in BCC and not in regular recipients or CC
    const isViewerOnlyInBcc = () => {
        if (!messageData || !viewerEmail) return false;
        
        // Check if viewer is in BCC
        const bccList = messageData.bcc ? messageData.bcc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const isInBcc = bccList.includes(viewerEmail.toLowerCase());
        
        // Check if viewer is also in regular recipients or CC (in which case Reply All should be available)
        const recipientList = messageData.recipient ? messageData.recipient.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        const ccList = messageData.cc ? messageData.cc.split(/[,;\s]+/).map(email => email.trim().toLowerCase()) : [];
        
        const isInRecipientOrCc = recipientList.includes(viewerEmail.toLowerCase()) || ccList.includes(viewerEmail.toLowerCase());
        
        // Return true only if viewer is in BCC but not in regular recipients or CC
        return isInBcc && !isInRecipientOrCc;
    };

    if (loading) {
        return (
            <div className="mailbox-view-loading">
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="mailbox-view-error-container">
                <Card className="mailbox-view-error-card">
                    <Result
                        status="error"
                        title={error}
                        subTitle={t('messages.view.errorSubtitle')}
                        extra={[
                            <Button 
                                key="close"
                                onClick={() => navigate(user ? '/profile/mailbox' : '/mailbox')}
                                icon={<CloseOutlined />}
                            >
                                {t('messages.view.close')}
                            </Button>
                        ]}
                    />
                </Card>
            </div>
        );
    }

    return (
        <div className="mailbox-view-container">
            {/* Back to Mailbox Header */}
            <div className="mailbox-view-top-header">
                {mailboxHref && (
                    <Button
                        type="text"
                        icon={<RollbackOutlined />}
                        onClick={() => navigate(mailboxHref)}
                        className="back-to-mailbox-btn"
                    >
                        {t('messages.view.backToMailbox')}
                    </Button>
                )}
                <div className="mailbox-view-action-buttons">
                    {(messageData?.cc || hasMultipleRecipients(messageData?.recipient)) && !isViewerOnlyInBcc() && (
                        <Button 
                            icon={<RollbackOutlined />}
                            onClick={handleReplyAll}
                            className="action-btn"
                        >
                            {t('messages.view.replyAll')}
                        </Button>
                    )}
                    <Button 
                        icon={<RollbackOutlined />}
                        onClick={handleReply}
                        className="action-btn"
                    >
                        {t('messages.view.reply')}
                    </Button>
                </div>
            </div>

            <div className="mailbox-view-detail-body-wrapper">
                {messageData?.self_destruct && (
                    <div className="mailbox-view-self-destruct-warning">
                        {t('messages.view.selfDestructWarning')}
                    </div>
                )}
                {messageData && (
                    <>
                        {/* Email Info Section */}
                        <div 
                            className={`mailbox-view-email-info ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                            onCopy={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    message.warning(t('messages.view.copyingDisabled'));
                                    return false;
                                }
                            }}
                            onContextMenu={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    return false;
                                }
                            }}
                        >
                            <div className="mailbox-view-icon-wrapper">
                                <MailOutlined />
                            </div>
                            <div className="mailbox-view-email-details">
                                <div className="mailbox-view-sender-receiver">
                                    <div className="mailbox-view-email-row">
                                        <Text strong>{t('messages.view.from')} </Text>
                                        <Text>{messageData.sender}</Text>
                                    </div>
                                    {messageData.recipient?.trim() && (
                                        <div className="mailbox-view-email-row">
                                            <Text strong>{t('messages.view.to')} </Text>
                                            <Text>{messageData.recipient}</Text>
                                        </div>
                                    )}
                                </div>
                                {messageData.cc?.trim() && (
                                    <div className="mailbox-view-cc-section">
                                        <div className="mailbox-view-email-row">
                                            <Text strong>{t('messages.view.cc')} </Text>
                                            <Text>{messageData.cc}</Text>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Metadata on the right side */}
                            <div className="mailbox-view-metadata-side">
                                {messageData.self_destruct && (
                                    <div className="mailbox-view-metadata-item">
                                        <div className="mailbox-view-metadata-label">{t('messages.view.selfDestruct')}</div>
                                        <div className="mailbox-view-metadata-value">
                                            {t('messages.view.enabled')}
                                        </div>
                                    </div>
                                )}
                                <div className="mailbox-view-metadata-item">
                                    <div className="mailbox-view-metadata-label">{t('messages.view.expires')}</div>
                                    <div className="mailbox-view-metadata-value">
                                        {dayjs(messageData.timestamp).format('MMM D, YYYY, h:mm A')}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Subject Section */}
                        <div 
                            className={`mailbox-view-subject-section ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                            onCopy={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    message.warning(t('messages.view.copyingDisabled'));
                                    return false;
                                }
                            }}
                            onContextMenu={(e) => {
                                if (messageData.prevent_message_copy) {
                                    e.preventDefault();
                                    return false;
                                }
                            }}
                        >
                            <Title level={3} className="mailbox-view-subject-title">
                                {messageData.subject}
                            </Title>
                        </div>

                        {/* Two-column layout: Body + Attachments */}
                        <div className="mailbox-view-content-row" ref={contentRowRef}>
                            {/* Message Body - Left Column */}
                            <div 
                                className="mailbox-view-body-column"
                                style={{ width: messageData.files?.length > 0 ? `${columnWidths.body}%` : '100%' }}
                            >
                                <div 
                                    className={`mailbox-view-body-section ${messageData.prevent_message_copy ? 'no-copy' : ''}`}
                                    onCopy={(e) => {
                                        if (messageData.prevent_message_copy) {
                                            e.preventDefault();
                                            message.warning(t('messages.view.copyingDisabled'));
                                            return false;
                                        }
                                    }}
                                    onContextMenu={(e) => {
                                        if (messageData.prevent_message_copy) {
                                            e.preventDefault();
                                            return false;
                                        }
                                    }}
                                >
                                    <div 
                                        className="mailbox-view-body-content"
                                        dangerouslySetInnerHTML={{ __html: messageData.body }}
                                    />
                                </div>
                            </div>

                            {/* Resize Handle */}
                            {messageData.files?.length > 0 && (
                                <div 
                                    className="mailbox-view-resize-handle"
                                    onMouseDown={(e) => { e.preventDefault(); setIsResizing(true); }}
                                />
                            )}

                            {/* Attachments - Right Column */}
                            {messageData.files?.length > 0 && (
                                <div 
                                    className="mailbox-view-attachments-column"
                                    style={{ width: `${columnWidths.attachments}%` }}
                                >
                                    <div className="mailbox-view-attachments-section">
                                        <div className="mailbox-view-attachments-section-header">
                                            <span className="mailbox-view-attachments-section-label">
                                                {t('messages.view.attachments', { count: messageData.files.length })}
                                            </span>
                                            <Tooltip title={t('messages.view.downloadAll')}>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<FolderOpenOutlined />}
                                                    onClick={handleDownloadAll}
                                                    className="mailbox-view-attachments-download-all-btn"
                                                />
                                            </Tooltip>
                                        </div>
                                        <div className="mailbox-view-attachments-list mailbox-view-attachments-list-rows">
                                            {messageData.files.map((attachment, index) => {
                                                const fileName = attachment.file_name;
                                                const fileSize = attachment.file_size;
                                                const formattedSize = typeof fileSize === 'number'
                                                    ? fileSize >= 1024 * 1024
                                                        ? `${(fileSize / (1024 * 1024)).toFixed(2)} MB`
                                                        : `${(fileSize / 1024).toFixed(2)} KB`
                                                    : fileSize || '-';
                                                const canPreview = isConvertible(fileName);
                                                const commentCount = attachment.comment_count ?? 0;
                                                return (
                                                    <div
                                                        key={attachment.unique_identifier || index}
                                                        className={`mailbox-view-attachment-row ${canPreview ? 'mailbox-view-attachment-row-previewable' : ''} ${canPreview && commentCount > 0 ? 'mailbox-view-attachment-row-has-comments' : ''}`}
                                                        onClick={canPreview ? () => setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' }) : undefined}
                                                        role={canPreview ? 'button' : undefined}
                                                        tabIndex={canPreview ? 0 : undefined}
                                                        onKeyDown={canPreview ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' }); } } : undefined}
                                                    >
                                                        {canPreview && commentCount > 0 && (
                                                            <span
                                                                className="mailbox-view-attachment-comment-badge"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                                }}
                                                                role="button"
                                                                tabIndex={0}
                                                                onKeyDown={(e) => {
                                                                    if ((e.key === 'Enter' || e.key === ' ') && commentCount > 0) {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                                    }
                                                                }}
                                                                aria-label={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}
                                                            >
                                                                <CommentOutlined />
                                                                <span className="mailbox-view-attachment-comment-badge-count">{commentCount}</span>
                                                            </span>
                                                        )}
                                                        <div className="mailbox-view-attachment-title-cell">
                                                            <span className="mailbox-view-attachment-row-title">
                                                                {fileName}
                                                            </span>
                                                            <span className="mailbox-view-attachment-row-filesize">
                                                                {formattedSize}
                                                            </span>
                                                        </div>
                                                        <div className="mailbox-view-attachment-actions">
                                                            {canPreview && (
                                                                <Button
                                                                    type="text"
                                                                    size="small"
                                                                    icon={<EyeOutlined />}
                                                                    className="mailbox-view-attachment-preview-btn"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setAttachmentPreviewDoc({ documentFileId: attachment.unique_identifier, fileName, source: 'messages' });
                                                                    }}
                                                                />
                                                            )}
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<DownloadOutlined />}
                                                                className="mailbox-view-attachment-download-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDownload(attachment.unique_identifier, fileName);
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <AttachmentPreview
                open={!!attachmentPreviewDoc}
                onClose={() => setAttachmentPreviewDoc(null)}
                documentFileId={attachmentPreviewDoc?.documentFileId}
                fileName={attachmentPreviewDoc?.fileName}
                source="messages"
                onDownload={attachmentPreviewDoc ? () => handleDownload(attachmentPreviewDoc.documentFileId, attachmentPreviewDoc.fileName) : undefined}
            />

            <AccessCodeModal
                open={requiresAccessCode}
                rid={unique_identifier}
                onSuccess={handleAccessCodeSuccess}
                onCancel={() => {
                    setRequiresAccessCode(false);
                    setError(t('messages.view.errorAccessCodeRequired') || 'Access code required to view this message');
                }}
            />
        </div>
    );
};

export default MessageView;